import React, { Dispatch, ReactNode, SetStateAction, createContext, useContext, useState } from 'react';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import classNames from 'classnames';

import { NavLink, paths } from '@starlingbank/shared';

import OnboardingContext from '../../context/OnboardingContext';
import { trackGtm } from '../../utils';
import { useSmallUpBp } from '../../hooks/useMedia';
import { MegaMenu } from './MegaMenu';

import * as styles from './SiteNavigation.module.scss';

import chevron from './assets/chevron.svg';
import starlingLogoOneLine from './assets/starling-logo-one-line.svg';
import starlingLogoTwoLines from './assets/starling-logo-two-lines.svg';
import burgerBars from './assets/burger/bars.svg';
import burgerCross from './assets/burger/cross.svg';

export type MenuDropDowns = 'pca' | 'bca' | 'about';

type OverlayProps = {
  children: ReactNode;
};

type LinkItemProps = {
  item: NavLink;
  additionalClasses?: string;
};

export const SiteNavigationContext = createContext<{
  activeMenu: string | null;
  setActiveMenu: Dispatch<SetStateAction<string | null>>;
}>({
  activeMenu: null,
  setActiveMenu: () => null
});

export const LinkItem = ({ item, additionalClasses }: LinkItemProps): JSX.Element => {
  const { activeMenu } = useContext(SiteNavigationContext);
  const itemValue = item.text.toLowerCase();
  const isExternal = item.href.startsWith('https://');

  const trackClick = () => {
    trackGtm({
      event: 'site_nav_click',
      parent: activeMenu || '',
      linkLabel: item.text,
      linkUrl: item.href
    });
  };

  return (
    <NavigationMenu.Item value={itemValue}>
      {item.children ? (
        <>
          <NavigationMenu.Trigger
            className={classNames(styles.linkTextLink, additionalClasses)}
            onPointerMove={(e) => e.preventDefault()}
            onPointerLeave={(e) => e.preventDefault()}
          >
            <span className={styles.linkWithChevron}>
              {item.text}{' '}
              <img
                src={chevron}
                alt="Toggle the Personal menu"
                className={classNames(styles.chevron, {
                  [styles.chevronDown]: activeMenu === itemValue
                })}
              />
            </span>
          </NavigationMenu.Trigger>
          <NavigationMenu.Content
            className={styles.subNavContent}
            onPointerMove={(e) => e.preventDefault()}
            onPointerLeave={(e) => e.preventDefault()}
          >
            <NavigationMenu.Sub orientation="vertical">
              <NavigationMenu.List className={styles.dropdown}>
                {item.children.map((item) => (
                  <LinkItem key={item.text} item={item} />
                ))}
              </NavigationMenu.List>
            </NavigationMenu.Sub>
          </NavigationMenu.Content>
        </>
      ) : (
        <NavigationMenu.Link
          className={classNames(styles.linkTextLink, additionalClasses)}
          href={item.href}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          onClick={() => trackClick()}
        >
          {item.text}
        </NavigationMenu.Link>
      )}
    </NavigationMenu.Item>
  );
};

const Overlay = ({ children }: OverlayProps): JSX.Element => {
  const { activeMenu } = useContext(SiteNavigationContext);
  return (
    <section
      className={classNames(styles.overlay, {
        [styles.isOpen]: activeMenu
      })}
    >
      {children}
    </section>
  );
};

const BurgerButton = () => {
  const { activeMenu } = useContext(SiteNavigationContext);

  return (
    <NavigationMenu.Item className={styles.burgerButtonItem} value={activeMenu ? activeMenu : 'pca'}>
      <NavigationMenu.Trigger
        className={styles.burgerButton}
        onPointerMove={(e) => e.preventDefault()}
        onPointerLeave={(e) => e.preventDefault()}
      >
        <img
          src={activeMenu ? burgerCross : burgerBars}
          width={42}
          height={42}
          alt="Open menu"
          className="mobile-nav"
        />
      </NavigationMenu.Trigger>
    </NavigationMenu.Item>
  );
};

export const SiteNavigation = ({ forCampaign = false }: { forCampaign?: boolean }): JSX.Element => {
  const { route: webflowRoute, hideCta: hideWebflowButton } = useContext(OnboardingContext);

  const {
    siteNavigation: { topRight }
  } = paths;

  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  const isTabletUp = useSmallUpBp();

  const updateMegaMenu = (selecton: MenuDropDowns) => {
    if (activeMenu === selecton) {
      setActiveMenu(null);
    } else {
      setActiveMenu(selecton);
    }
  };

  const webflowDestination = () => {
    let newLocation;

    if (webflowRoute) {
      if (webflowRoute === 'default') {
        newLocation = '/apply/';
      } else {
        const params = new URLSearchParams();
        params.set('r', webflowRoute);
        newLocation = `/apply/?${params.toString()}`;
      }
    } else {
      newLocation = '/apply/';
    }

    return newLocation;
  };

  const handleWebflowNavigation = () => {
    trackGtm({
      event: 'onboarding_cta_click',
      placement: 'nav_bar'
    });
  };

  return (
    <SiteNavigationContext.Provider value={{ activeMenu, setActiveMenu }}>
      <Overlay>
        <NavigationMenu.Root
          data-sitenavigation
          className={styles.container}
          onValueChange={(item) => setActiveMenu(item)}
        >
          <div className={styles.shadow}>
            <div
              className={classNames(styles.nav, {
                [styles.forCampaign]: forCampaign
              })}
            >
              <div>
                <a className={styles.skipLink} href="#main-content">
                  Skip to main content
                </a>
              </div>

              <div className={styles.logoColumn}>
                <a href="/" aria-label="Link to homepage">
                  <img
                    src={starlingLogoOneLine}
                    className={styles.logoOne}
                    width={257}
                    height={38}
                    alt="Starling Bank logo"
                  />
                  <img
                    src={starlingLogoTwoLines}
                    className={styles.logoTwo}
                    width={137}
                    height={42}
                    alt="Starling Bank logo"
                  />
                </a>
              </div>

              <div className={styles.accountSelectColumn}>
                <NavigationMenu.List className={classNames(styles.list, styles.accountTypes)}>
                  <NavigationMenu.Item value="pca">
                    <NavigationMenu.Trigger
                      className={classNames(styles.linkPca, {
                        [styles.activePca]: activeMenu === 'pca'
                      })}
                      onClick={() => updateMegaMenu('pca')}
                      onPointerMove={(e) => e.preventDefault()}
                      onPointerLeave={(e) => e.preventDefault()}
                    >
                      Personal{' '}
                      <img
                        src={chevron}
                        alt="Toggle the Personal menu"
                        className={classNames(styles.chevron, {
                          [styles.chevronDown]: activeMenu === 'pca'
                        })}
                      />
                    </NavigationMenu.Trigger>
                    <NavigationMenu.Content
                      onPointerMove={(e) => e.preventDefault()}
                      onPointerLeave={(e) => e.preventDefault()}
                    >
                      <MegaMenu activeMenu="pca" updateMegaMenu={updateMegaMenu} />
                    </NavigationMenu.Content>
                  </NavigationMenu.Item>
                  <NavigationMenu.Item value="bca">
                    <NavigationMenu.Trigger
                      className={classNames(styles.linkBca, {
                        [styles.activeBca]: activeMenu === 'bca'
                      })}
                      onClick={() => updateMegaMenu('bca')}
                      onPointerMove={(e) => e.preventDefault()}
                      onPointerLeave={(e) => e.preventDefault()}
                    >
                      Business{' '}
                      <img
                        src={chevron}
                        alt="Toggle the Personal menu"
                        className={classNames(styles.chevron, {
                          [styles.chevronDown]: activeMenu === 'bca'
                        })}
                      />
                    </NavigationMenu.Trigger>
                    <NavigationMenu.Content
                      onPointerMove={(e) => e.preventDefault()}
                      onPointerLeave={(e) => e.preventDefault()}
                    >
                      <MegaMenu activeMenu="bca" updateMegaMenu={updateMegaMenu} />
                    </NavigationMenu.Content>
                  </NavigationMenu.Item>
                </NavigationMenu.List>
              </div>

              <div className={styles.rightLinksColumn}>
                <NavigationMenu.List className={classNames(styles.list, styles.rightLinksList)}>
                  {topRight.map((item) => (
                    <LinkItem key={item.text} item={item} additionalClasses={styles.laptopUp} />
                  ))}
                  {!hideWebflowButton ? (
                    <NavigationMenu.Item>
                      <a
                        href={webflowDestination()}
                        onClick={handleWebflowNavigation}
                        id="download-nav-cta"
                        className={classNames('btn-download-app', styles.applyNowButton)}
                      >
                        Apply&nbsp;now
                      </a>
                    </NavigationMenu.Item>
                  ) : null}
                  <BurgerButton />
                </NavigationMenu.List>
              </div>
            </div>
          </div>
          {isTabletUp ? <></> : <NavigationMenu.Viewport />}
        </NavigationMenu.Root>
      </Overlay>
    </SiteNavigationContext.Provider>
  );
};
